import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast, VehicleCard, DirectoryCardSkeleton } from '@/components';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import { EngineService } from '@/services/engine';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useResetQueryState } from '@/hooks';
import type { Vehicle, UpdateVehiclePayload } from '@/types';
import { VehicleType } from '@/types/vehicle';
import { DirectoryGrid } from '@/features/DirectoryGrid';
import type { DirectoryGridConfig } from '@/features/DirectoryGrid';
import { MergeVehicleModal } from '@/features/MergeIdentity/vehicle/MergeVehicleModal';
import { AddVehicleModal } from './components/AddVehicleModal';
import { DeleteVehicleConfirmationModal } from './components/DeleteVehicleConfirmationModal';
import { useDuplicatePlateMerge } from './hooks/useDuplicatePlateMerge';

const SORT_OPTIONS = [
    { value: 'date_newest', labelKey: 'browseBar.createdAtNewest' },
    { value: 'date_oldest', labelKey: 'browseBar.createdAtOldest' },
    { value: 'last_seen_newest', labelKey: 'browseBar.lastSeenNewest' },
    { value: 'last_seen_oldest', labelKey: 'browseBar.lastSeenOldest' },
    { value: 'plate_a_z', labelKey: 'browseBar.plateAZ' },
    { value: 'plate_z_a', labelKey: 'browseBar.plateZA' },
    { value: 'occurrences_high_low', labelKey: 'browseBar.occurrencesHighLow' },
    { value: 'occurrences_low_high', labelKey: 'browseBar.occurrencesLowHigh' },
];

const ORDER_BY_MAP: Record<string, string> = {
    date_newest: 'createdAt desc',
    date_oldest: 'createdAt asc',
    last_seen_newest: 'lastSeen desc',
    last_seen_oldest: 'lastSeen asc',
    plate_a_z: 'licensePlate asc',
    plate_z_a: 'licensePlate desc',
    occurrences_high_low: 'occurrences desc',
    occurrences_low_high: 'occurrences asc',
};

export function VehicleGrid() {
    useResetQueryState();
    const { t } = useTranslation([
        I18nNamespace.DIRECTORY,
        I18nNamespace.COMMON,
    ]);
    const navigate = useNavigate();
    const [mergeSourceVehicle, setMergeSourceVehicle] =
        useState<Vehicle | null>(null);
    const revalidateRef = useRef<(() => Promise<void>) | null>(null);

    const openMergeModal = (vehicle: Vehicle) => {
        setMergeSourceVehicle(vehicle);
    };

    const navigateToVehicle = (vehicleId: string) => {
        void revalidateRef.current?.();
        navigate(generatePath(AppRoutes.VEHICLE, { vehicleId }));
    };

    const { handleUpdateError, dialog: duplicatePlateDialog } =
        useDuplicatePlateMerge({
            onMerged: (result) => navigateToVehicle(result.targetVehicle.id),
            onSourceStale: () => void revalidateRef.current?.(),
        });

    const config: DirectoryGridConfig<Vehicle, UpdateVehiclePayload> = {
        title: t('common:pageTitle.vehicles'),
        parentTitle: t('common:navigation.directory'),
        pageKey: 'vehicles',
        cacheKeyPrefix: CACHE_KEYS.ALL_VEHICLES,
        statsCacheKey: CACHE_KEYS.ALL_VEHICLES_STATS,
        fetchItems: (params) =>
            EngineService.getVehicles({
                page: params.page,
                size: params.size,
                search: params.search,
                known: params.known,
                tags: params.tags,
                vehicleTypes: params.vehicleTypes as VehicleType[] | undefined,
                orderBy: params.orderBy,
            }),
        fetchStats: () => EngineService.getVehiclesStats(),
        fetchTags: () => EngineService.getVehicleTags(),
        tagsCacheKey: CACHE_KEYS.ALL_VEHICLES_TAGS,
        vehicleTypeOptions: Object.values(VehicleType),
        sortOptions: SORT_OPTIONS,
        defaultSort: 'date_newest',
        orderByMap: ORDER_BY_MAP,
        itemRoute: AppRoutes.VEHICLE,
        routeParamKey: 'vehicleId',
        searchPlaceholder: t('vehicles.searchPlaceholder'),
        tabLabels: {
            all: t('vehicles.tabs.all'),
            unknown: t('vehicles.tabs.unknown'),
            recognized: t('vehicles.tabs.recognized'),
        },
        messages: {
            empty: t('vehicles.noVehiclesFound'),
            loadingMore: t('vehicles.loadingMore'),
            allLoaded: t('vehicles.allVehiclesLoaded'),
            failedToLoad: t('vehicles.failedToLoad'),
        },
        selectionEntityLabel: t('common:pageTitle.vehicles'),
        bulkDeleteItems: (selection, filters) =>
            EngineService.deleteVehicles(selection, filters),
        canDeleteItem: (vehicle) => !vehicle.known,
        markAsUnknownUpdate: () => ({
            payload: { name: null, tags: [] },
            optimisticPatch: { name: null, tags: [], known: false },
        }),
        updateItem: (id, payload) => EngineService.updateVehicle(id, payload),
        onUpdateSuccess: (mode) =>
            toast.success(
                t(
                    mode === 'edit'
                        ? 'vehicles.editModal.success'
                        : 'vehicles.recognizeModal.success',
                ),
            ),
        onUpdateError: (mode, context) => {
            if (handleUpdateError(context.error, context.itemId)) {
                return;
            }
            toast.error(
                t(
                    mode === 'edit'
                        ? 'vehicles.editModal.error'
                        : 'vehicles.recognizeModal.error',
                ),
            );
        },
        onDeleteSuccess: (deleted) =>
            toast.success(t('vehicles.deleteSuccess', { count: deleted })),
        onDeleteError: () => toast.error(t('vehicles.deleteError')),
        onPartialDelete: ({ deleted, failed }) =>
            toast.error(
                t('vehicles.deletePartial', { count: deleted, failed }),
            ),
        onDeleteInProgress: () => toast.info(t('vehicles.deleteInProgress')),
        onMarkAsUnknownSuccess: () =>
            toast.success(t('vehicles.markAsUnknown.success')),
        onMarkAsUnknownError: () =>
            toast.error(t('vehicles.markAsUnknown.error')),
        onMergeSingleItem: openMergeModal,
    };

    return (
        <>
            <DirectoryGrid<Vehicle, UpdateVehiclePayload>
                config={config}
                renderCard={(vehicle, handlers) => (
                    <VehicleCard
                        key={vehicle.id}
                        vehicle={vehicle}
                        thumbnailUrl={vehicle.thumbnailUrl}
                        checked={handlers.checked}
                        isDeleting={handlers.isDeleting}
                        selectionDisabled={handlers.selectionDisabled}
                        onClick={handlers.onClick}
                        onEdit={handlers.onEdit}
                        onDelete={handlers.onDelete}
                        onMarkAsRecognized={handlers.onMarkAsRecognized}
                        onMarkAsUnknown={handlers.onMarkAsUnknown}
                        onMerge={handlers.onMerge}
                    />
                )}
                renderEditModal={(props) => (
                    <AddVehicleModal
                        isOpen={props.isOpen}
                        onClose={props.onClose}
                        isSubmitting={props.isSubmitting}
                        mode={props.mode}
                        vehicle={props.item}
                        onUpdate={props.onUpdate}
                    />
                )}
                renderDeleteConfirmation={(props) => (
                    <DeleteVehicleConfirmationModal
                        isOpen={props.isOpen}
                        deleteInProgress={props.isDeleting}
                        count={props.count}
                        onConfirm={props.onConfirm}
                        onCancel={props.onCancel}
                    />
                )}
                skeletonComponent={<DirectoryCardSkeleton />}
                onRevalidateRef={revalidateRef}
            />
            {mergeSourceVehicle && (
                <MergeVehicleModal
                    isOpen={mergeSourceVehicle !== null}
                    onClose={() => setMergeSourceVehicle(null)}
                    sourceVehicle={mergeSourceVehicle}
                    onSuccess={(result) => {
                        setMergeSourceVehicle(null);
                        navigateToVehicle(result.targetVehicle.id);
                    }}
                    onSourceStale={() => {
                        void revalidateRef.current?.();
                    }}
                />
            )}
            {duplicatePlateDialog}
        </>
    );
}
